import { useEffect, useMemo, useRef, useState } from "react"
import { useLanguage, useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { useMeetingsStore } from "../../store/meetings.store"
import { meetingsService } from "../../services/meetings.service"
import { meetingsApi } from "../../services/meetings.api"
import { resolveStartMs, settledSeats, typedSeats, validateTitle } from "../../utils/meetings.validators"
import { meetingCredentialsRepo } from "../../services/meetings.repo"
import { isMeetingError, isValidationError } from "../../services/meetings-error"
import { generatePassword as serviceGeneratePassword } from "../../services/password.crypto"
import type { MeetingSettings } from "../../models/meeting.model"
import { useAuthStore } from "@/features/auth/store/auth.store"
import { ButtonLoader, FullPageDotsLoader } from "@/clientWeb/components/ui/page-loader"
import { Eye, Users, Settings, ChevronDown, ChevronUp, Video, Mic, Globe, Check, Sparkles, Clock as ClockIcon, Zap, Shield, Calendar as CalendarIcon, MapPin, Monitor, RefreshCw, Upload, X, FileText, Phone, UserPlus2, Search, Square, CheckSquare, Info } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import { Input } from "@/clientWeb/components/ui/input"
import { Textarea } from "@/clientWeb/components/ui/textarea"
import { Badge } from "@/clientWeb/components/ui/badge"
import { Label } from "@/clientWeb/components/ui/label"
import { Card } from "@/clientWeb/components/ui/card"
import { Switch } from "@/clientWeb/components/ui/switch"
import { DatePicker } from "@/clientWeb/components/ui/date-picker"
import { TimePicker } from "@/clientWeb/components/ui/time-picker"
import assetRoomsNoData from '@/assets/RoomsNoData.svg'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import { TimezoneSelect, formatTimezoneLabel, getDefaultTimezone } from "@/clientWeb/components/ui/timezone-select"
import { canonicalTimezone, utcMsToZonedParts } from "@/shared/utils"
import TimeZonePlannerDialog from "./TimeZonePlannerDialog"
import { DUMMY_CONTACTS, getContactsByEmails, type Contact } from "@/features/meetings/data/contacts"
import { contactsApi } from "../../services/contacts.api"
import { uploadShareFile, deleteUploadedShare, renderableMime, MAX_SHARE_UPLOAD_BYTES } from "@/features/meeting/services/share-upload"

import { isEmailAddress, mergeEmails, planInviteeAdd } from "@/features/meetings/services/invitee-add"
import { ATTACHABLE_ACCEPT, partitionPickedAttachments } from "@/features/meetings/services/attachment-picking"
import { MEETING_PASSWORD_LENGTH, MEETING_PASSWORD_MAX_LENGTH, MEETING_PASSWORD_MIN_LENGTH, MEETING_SEATS_MIN } from "@/features/meetings/constants/meetings.constants"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/clientWeb/components/ui/tooltip"
import { earliestTimeFor, isBefore } from "@/features/meetings/utils/schedule-time"
import {
  END_DATE_PROBLEM_KEY,
  defaultRecurrence,
  followStartDate,
  recurrenceEndDateProblem,
  recurrenceFromConfig,
  recurrenceToConfig,
  type RecurrenceState,
} from "@/features/meetings/utils/recurrence"
import { RecurrencePicker } from "./RecurrencePicker"
import { describeRecurrence, formatOccurrenceDay } from "@/features/meetings/utils/recurrence"
import { useHostMeetingPassword } from "../../hooks/useHostMeetingPassword"

/** Minute granularity of the time picker, and of the floor it is held to. */
const TIME_STEP = 5

/**
 * "Now", as the wall clock in `timeZone` reads it, wrapped back into a plain
 * `Date` built from local getters. `earliestTimeFor`/`isSameDay` only ever
 * read a Date through `getFullYear`/`getHours`/etc., so handing them a Date
 * whose LOCAL fields already hold the target zone's numbers is enough to
 * make the picker's "today" floor track the selected zone instead of the
 * browser's.
 */
const zonedNow = (timeZone: string): Date => {
  const p = utcMsToZonedParts(Date.now(), timeZone)
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute)
}

/** How many files a meeting may carry. Shared with the picker's own cap check. */
const MAX_ATTACHMENTS = 5

/**
 * One switch row under Advanced Options. The hover tint only shows which switch
 * a line of text belongs to: the row itself is not clickable, so a stray click
 * on the text cannot flip a setting; only the switch does. -mx-2/px-2 lets the
 * tint overhang without moving the icons out of line with the rest of the form.
 */
const OPTION_ROW =
  "-mx-2 flex items-center justify-between gap-x-3 rounded-lg px-2 py-1.5 transition-colors hover:bg-black/[0.06] dark:hover:bg-black/25"
/** The same row while its switch is locked: no tint. */
const OPTION_ROW_LOCKED =
  "-mx-2 flex items-center justify-between gap-x-3 rounded-lg px-2 py-1.5"

interface AttachmentFile {
  id: string
  name: string
  size: number
  type: string
  url?: string
  /** Already on the saved meeting, as opposed to uploaded during this edit.
   *  Decides whether removing it may delete the file right away. */
  saved?: boolean
  /** The PDF copy of a PowerPoint, Word or text file, which is what the
   *  meeting presents. Absent for a PDF, and when none could be made. */
  previewUrl?: string
  /** The copy's attachment id, so removing the file removes the copy too. */
  previewId?: string
}

/**
 * The file-type tile on an attachment row.
 *
 * Every row used to carry the same grey document glyph, so an uploaded PDF was
 * not recognisable as one. A PDF now gets the red PDF badge the messenger's
 * document cards use; anything else keeps a neutral tile rather than being
 * labelled as something it is not. The type decides, and the name only when
 * the type says nothing — older records and some uploads come back as
 * `application/octet-stream` (see `renderableMime`).
 */
function AttachmentIcon({ name, type }: { name: string; type?: string }) {
  const isPdf = renderableMime(type ?? "", name).toLowerCase().startsWith("application/pdf")
  return (
    <div className={`w-9 h-9 rounded-lg flex flex-col items-center justify-center shrink-0 ${isPdf ? "bg-red-500 text-white" : "bg-muted text-muted-foreground"}`}>
      <FileText className="w-4 h-4" />
      {isPdf && <span className="text-[8px] font-bold mt-0.5 leading-none">PDF</span>}
    </div>
  )
}

interface AttendanceConfig {
  enabled: boolean
  requireApproval: boolean
  allowLateJoin: boolean
}


interface InvitesPanelProps {
  selectedContacts: string[]
  onContactsChange: (contacts: string[]) => void
  /** Reports each invitee's kind, keyed by lowercased email. The parent splits
   *  at submit from this plus the invitee list — deriving two mirrored arrays
   *  in an effect meant any other setState could clobber them between the
   *  effect running and the form being submitted. */
  onStatusesChange?: (statuses: Record<string, "active" | "passive">) => void
  /** The organiser. Always present, never removable — they are in the meeting
   *  they are scheduling, so offering to un-invite them is meaningless. */
  hostEmail?: string
  /** Passive invitees loaded from a saved meeting. Without this the panel has
   *  no way to know an existing address was invited by email — the directory
   *  cannot say so — and edit mode listed every invitee as active. */
  initialPassive?: string[]
  /**
   * Does this meeting have two audiences?
   *
   * Off, the panel has no tabs and every invitee is a full participant — there
   * is no second kind of attendee to sort anyone into, so a chooser between
   * them is a decision with no meaning. On, the tabs decide who gets which of
   * the meeting's two links.
   */
  passiveEnabled: boolean
  /**
   * How many people the meeting has room for, the organiser included.
   *
   * The panel is where somebody finds out they have run out — by the time the
   * form is submitted they have typed a dozen addresses and have to work out
   * which to drop.
   */
  seats?: number
}

function InvitesPanel({ selectedContacts, onContactsChange, onStatusesChange, hostEmail, initialPassive, passiveEnabled, seats }: InvitesPanelProps) {
  const t = useTranslation()
  const [showInviteInterface, setShowInviteInterface] = useState(false)
  const [activeTab, setActiveTab] = useState<"active" | "passive">("active")
  const [searchQuery, setSearchQuery] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [pendingSelections, setPendingSelections] = useState<string[]>([])
  /**
   * pendingSelections as it stood when the dropdown opened — lets planInviteeAdd
   * tell that seeded-but-untouched baseline apart from a real tick/untick.
   */
  const dropdownBaseline = useRef<string[]>([])
  /**
   * Which tab each invitee was added from, for addresses the directory does not
   * know. getContactsByEmails synthesizes those with a hardcoded
   * `status: 'active'`, so anything typed on the Passive tab was listed under
   * Active — the opposite of where the person put it. A directory contact keeps
   * its real status; this only supplies one that would otherwise be invented.
   */
  const [addedAsStatus, setAddedAsStatus] = useState<Record<string, "active" | "passive">>({})

  // Turning the option off puts the panel back on its only remaining audience,
  // so re-enabling it later opens on Active rather than on a Passive tab left
  // selected from before.
  useEffect(() => {
    if (!passiveEnabled) setActiveTab("active")
  }, [passiveEnabled])

  // Seed from the saved meeting once it arrives, so reopening one keeps each
  // invitee on the tab they were invited from.
  useEffect(() => {
    if (!initialPassive?.length) return
    setAddedAsStatus((prev) => {
      const next = { ...prev }
      for (const email of initialPassive) {
        const key = email.trim().toLowerCase()
        if (key && !next[key]) next[key] = "passive"
      }
      return next
    })
  }, [initialPassive])
  const CONTACTS_PAGE_SIZE = 20
  const [contacts, setContacts] = useState<Contact[]>([])
  const [contactsTotal, setContactsTotal] = useState(0)
  const [contactsOffset, setContactsOffset] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  // Search-driven: only fetch when the user has typed. Each new query resets
  // pagination to page 0. Debounce 250ms so we don't fire per keystroke.
  useEffect(() => {
    const q = searchQuery.trim()
    if (!q) {
      setContacts([])
      setContactsTotal(0)
      setContactsOffset(0)
      setIsSearching(false)
      return
    }

    let cancelled = false
    setIsSearching(true)
    const handle = setTimeout(() => {
      contactsApi.list({ q, limit: CONTACTS_PAGE_SIZE, offset: 0 })
        .then(({ contacts: fetched, total }) => {
          if (cancelled) return
          const page = fetched ?? []
          setContacts(page)
          setContactsTotal(total ?? 0)
          setContactsOffset(page.length)
        })
        .catch(() => {
          if (cancelled) return
          setContacts([])
          setContactsTotal(0)
          setContactsOffset(0)
        })
        .finally(() => {
          if (!cancelled) setIsSearching(false)
        })
    }, 250)

    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [searchQuery])

  const hasMoreContacts = contacts.length < contactsTotal

  const loadMoreContacts = async () => {
    const q = searchQuery.trim()
    if (!q || isLoadingMore || !hasMoreContacts) return
    setIsLoadingMore(true)
    try {
      const { contacts: fetched, total } = await contactsApi.list({
        q, limit: CONTACTS_PAGE_SIZE, offset: contactsOffset,
      })
      const page = fetched ?? []
      setContacts((prev) => [...prev, ...page])
      setContactsTotal(total ?? 0)
      setContactsOffset((prev) => prev + page.length)
    } catch { /* silent — user can retry */ }
    finally { setIsLoadingMore(false) }
  }

  // selectedContacts holds EMAILS (matches backend). Resolve to Contact objects,
  // synthesizing entries for free-form emails not in the directory.
  const selectedContactObjects = getContactsByEmails(selectedContacts, contacts)

  const activeUsers = contacts.filter(c => c.status === "active")
  const passiveUsers = contacts.filter(c => c.status === "passive")

  // With one audience there is one directory. Filtering to `status === active`
  // would quietly hide every passive-status contact from a meeting where the
  // distinction has been switched off — they are simply people to invite.
  const currentUsers = !passiveEnabled
    ? contacts
    : activeTab === "active" ? activeUsers : passiveUsers
  const filteredUsers = currentUsers.filter(user =>
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const trimmedQuery = searchQuery.trim()
  const queryIsEmail = isEmailAddress(trimmedQuery)
  const queryAlreadyKnown = contacts.some(c => c.email.toLowerCase() === trimmedQuery.toLowerCase())
  const showInviteByEmailRow = queryIsEmail && !queryAlreadyKnown

  const openDropdown = () => {
    if (!showDropdown) {
      setPendingSelections([...selectedContacts])
      dropdownBaseline.current = [...selectedContacts]
    }
    setShowDropdown(true)
  }

  const togglePending = (email: string) => {
    setPendingSelections(prev =>
      prev.includes(email) ? prev.filter(e => e !== email) : [...prev, email]
    )
  }

  /**
   * Add typed addresses to whatever the current selection is.
   *
   * The base is `pendingSelections` while the dropdown is open — openDropdown
   * seeds it from selectedContacts, so it is the authoritative set and ticking
   * a name off it must still remove them — and `selectedContacts` otherwise,
   * for a straight type-and-Enter with no dropdown ever opened, which would
   * otherwise start from an empty list and drop everyone already invited.
   */
  const addEmails = (emails: string[]) => {
    const base = showDropdown ? pendingSelections : selectedContacts
    // Case-insensitively, so `John@instavc.com` after `john@instavc.com` is the
    // same person and not a second invitation.
    const { merged, added } = mergeEmails(base, emails)

    setAddedAsStatus((prev) => {
      const next = { ...prev }
      for (const email of added) {
        // A contact the directory knows keeps its own status, the same rule
        // `confirmAdd` applies to a ticked name. Filing a known colleague under
        // whichever tab happened to be open is what put an invitee in the list
        // the user was not looking at, which reads as nothing having happened.
        const known = contacts.find(c => c.email.toLowerCase() === email.toLowerCase())
        next[email.toLowerCase()] = known ? known.status : activeTab
      }
      return next
    })
    onContactsChange(merged)
    setPendingSelections(merged)
    setSearchQuery("")
    setShowDropdown(false)
  }

  /**
   * Add whatever is ready. Enter, the Add button and the "Invite …" row all
   * come here, so they cannot mean different things — see `planInviteeAdd`.
   */
  const commitAdd = () => {
    // The same base `addEmails` merges into, so "already invited" means what
    // the list about to be written says, not what it said before the dropdown
    // was opened and somebody unticked half of it.
    const invited = showDropdown ? pendingSelections : selectedContacts
    const plan = planInviteeAdd({
      typed: searchQuery,
      ticked: pendingSelections,
      baseline: showDropdown ? dropdownBaseline.current : selectedContacts,
      invited,
      seats,
    })

    switch (plan.action) {
      case "add-typed":
        addEmails([plan.email])
        return
      case "confirm-ticked":
        confirmAdd()
        return
      case "reject":
        if (plan.reason === "already-invited") {
          toast.error({
            title: t("meetings.schedule.alreadyInvited"),
            description: plan.text,
          })
        } else if (plan.reason === "seats-exceeded") {
          toast.error({
            title: t("meetings.schedule.notEnoughSeats"),
            description: t("meetings.schedule.notEnoughSeatsDesc", {
              seats: plan.seats,
              wanted: plan.wanted,
            }),
          })
        } else {
          toast.error({
            title: t("meetings.schedule.invalidEmail"),
            description: plan.text,
          })
        }
        return
      default:
        return
    }
  }

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return
    e.preventDefault()
    commitAdd()
  }

  /**
   * The ticked set replaces the invitee list rather than merging into it —
   * pendingSelections was seeded from it, so an unticked name has to come off.
   */
  const confirmAdd = () => {
    // Record the tab for these too, not just typed addresses. Clearing the
    // search below empties `contacts`, so getContactsByEmails re-synthesizes
    // every invitee with its hardcoded status: 'active' — a contact ticked on
    // the Passive tab was added and then listed under Active, which looks
    // exactly like nothing having been added at all.
    setAddedAsStatus((prev) => {
      const next = { ...prev }
      for (const email of pendingSelections) {
        const clean = email.trim()
        if (!clean) continue
        // A contact the directory knows keeps its own status; only supply one
        // that would otherwise be invented.
        const known = contacts.find(c => c.email.toLowerCase() === clean.toLowerCase())
        next[clean.toLowerCase()] = known ? known.status : activeTab
      }
      return next
    })
    onContactsChange(pendingSelections)
    setSearchQuery("")
    setShowDropdown(false)
  }

  const cancelDropdown = () => {
    setPendingSelections([])
    setSearchQuery("")
    setShowDropdown(false)
  }

  const toggleContact = (email: string) => {
    // The organiser stays. They are in the meeting they are scheduling, and
    // the API adds them back regardless — removing them here would only
    // produce a list that disagrees with what gets saved.
    if (isHostEmail(email)) return
    const newContacts = selectedContacts.includes(email)
      ? selectedContacts.filter(e => e !== email)
      : [...selectedContacts, email]
    onContactsChange(newContacts)
  }

  // A contact the directory knows keeps its own status; anything else is
  // grouped by the tab it was added from rather than the synthesized default.
  const isHostEmail = (email: string) =>
    !!hostEmail && email.toLowerCase() === hostEmail.toLowerCase()

  const statusOf = (u: Contact): "active" | "passive" =>
    // One audience means one kind of invitee. A directory contact carrying
    // `status: passive` would otherwise be filed as a view-only attendee of a
    // meeting that has no view-only mode, and land in `passiveinvities` on a
    // payload whose meeting issues only one link.
    !passiveEnabled ? "active" :
    // The organiser is an account by definition, whatever tab was open.
    isHostEmail(u.email) ? "active" :
    // `addedAsStatus` first: it was recorded at add time, when the directory
    // entry was still in hand. `contacts` is emptied when the search clears, so
    // checking it here would report every invitee as unknown.
    addedAsStatus[u.email.toLowerCase()] ?? u.status
  const selectedActiveUsers = selectedContactObjects.filter(u => statusOf(u) === "active")
  const selectedPassiveUsers = selectedContactObjects.filter(u => statusOf(u) === "passive")

  // Publish the kind of every selected invitee. Only this panel can know it —
  // the directory cannot say whether a typed address is passive — and the
  // parent needs it at submit, long after the tab has changed.
  useEffect(() => {
    // Only addresses this panel recorded a kind for. Two panels are mounted at
    // once — the desktop one and the mobile one — each with its own
    // addedAsStatus. Reporting a kind for every invitee meant the idle panel,
    // which has recorded nothing, published "active" for all of them and
    // overwrote what the panel you were actually using had just reported.
    const statuses: Record<string, "active" | "passive"> = {}
    for (const u of selectedContactObjects) {
      const key = u.email.toLowerCase()
      const recorded = !passiveEnabled
        ? "active"
        : isHostEmail(u.email) ? "active" : addedAsStatus[key]
      if (recorded) statuses[key] = recorded
    }
    onStatusesChange?.(statuses)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContacts, addedAsStatus, hostEmail, passiveEnabled])
  // With the tabs hidden there is no tab to name, and "No Active Users added
  // yet" reads as though a second, passive list were sitting somewhere else.
  const tabLabel = !passiveEnabled
    ? t("meetings.schedule.invitees")
    : activeTab === "active" ? t("meetings.schedule.activeUsers") : t("meetings.schedule.passiveUsers")
  const selectedActiveCount = selectedActiveUsers.length
  const selectedPassiveCount = selectedPassiveUsers.length

  const handleAddInvite = () => {
    setShowInviteInterface(true)
  }

  // Show empty state if no interface is shown and no contacts selected
  if (!showInviteInterface && selectedContacts.length === 0) {
    return (
      <Card className="flex-1 rounded-3xl border border-stroke-100 dark:border-border bg-card backdrop-blur">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-foreground">{t("meetings.schedule.invitees")}</h2>
            <Button 
              size="sm" 
              className="bg-primary hover:bg-primary/90 text-white px-4"
              onClick={handleAddInvite}
            >
              {t("meetings.schedule.addInvite")}
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{t("meetings.schedule.selectFromContacts")}</p>
        </div>

        {/* Empty State */}
        <div className="flex flex-col h-full items-center justify-center py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-muted/30 flex items-center justify-center mb-6">
            <img src={assetRoomsNoData} alt=""/>
          </div>
          <p className="text-base text-muted-foreground font-medium">{t("meetings.schedule.noInviteesAdded")}</p>
        </div>
      </Card>
    )
  }

  return (
    <Card className="flex-1 rounded-3xl border border-stroke-100 dark:border-border bg-card backdrop-blur">
      {/* Header */}
      <div className="px-6 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Invitees</h2>
          <Button 
            size="sm" 
            className="bg-primary hover:bg-primary/90 text-white px-4"
            onClick={handleAddInvite}
          >
            + ADD INVITE
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">Select from contacts or add email ID</p>
      </div>

      {/* Tabs */}
      {passiveEnabled && (
      <div className="px-6 pt-4">
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab("active")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "active"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            • {t("meetings.schedule.activeUsers")}
            {selectedActiveCount > 0 && (
              <span className="ml-2 text-xs bg-primary text-white rounded-full px-2 py-0.5">
                {selectedActiveCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("passive")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "passive"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            • {t("meetings.schedule.passiveUsers")}
            {selectedPassiveCount > 0 && (
              <span className="ml-2 text-xs bg-primary text-white rounded-full px-2 py-0.5">
                {selectedPassiveCount}
              </span>
            )}
          </button>
        </div>
      </div>
      )}

      {/* Search with Dropdown */}
      <div className="px-6 py-4 relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder={t("meetings.schedule.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              if (e.target.value.length > 0) openDropdown()
              else setShowDropdown(false)
            }}
            onFocus={() => { if (searchQuery.length > 0) openDropdown() }}
            onKeyDown={handleSearchKeyDown}
            className="pl-10 h-11 border-primary/40 focus:border-primary"
          />
        </div>

        {/* Search Dropdown */}
        {showDropdown && searchQuery && (
          <div className="absolute top-full left-6 right-6 mt-1 bg-card border border-border rounded-xl shadow-lg z-50">
            {!isSearching && contactsTotal > 0 && (
              <div className="px-3 py-1.5 text-[11px] text-muted-foreground border-b border-border/50">
                Showing {contacts.length} of {contactsTotal}
              </div>
            )}
            <div className="max-h-52 overflow-y-auto">
              {isSearching ? (
                <div className="p-4 text-center text-sm text-muted-foreground">Searching…</div>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const isPending = pendingSelections.includes(user.email)
                  return (
                    <div
                      key={user.email}
                      className="flex items-center gap-3 p-3 hover:bg-muted/40 cursor-pointer border-b border-border/50 last:border-b-0"
                      onClick={() => togglePending(user.email)}
                    >
                      <div className="shrink-0 text-primary">
                        {isPending
                          ? <CheckSquare className="w-5 h-5" />
                          : <Square className="w-5 h-5 text-muted-foreground" />
                        }
                      </div>
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold shrink-0">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      </div>
                    </div>
                  )
                })
              ) : !showInviteByEmailRow ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No {tabLabel} {t("meetings.schedule.noUsersFound")}
                </div>
              ) : null}
              {!isSearching && hasMoreContacts && (
                <button
                  type="button"
                  onClick={loadMoreContacts}
                  disabled={isLoadingMore}
                  className="w-full text-center text-xs py-2 text-primary hover:bg-primary/5 border-t border-border/50 disabled:opacity-60"
                >
                  {isLoadingMore ? "Loading…" : "Load more"}
                </button>
              )}
              {showInviteByEmailRow && (
                <div
                  className="flex items-center gap-3 p-3 hover:bg-primary/5 cursor-pointer border-t border-border/50"
                  onClick={commitAdd}
                >
                  <div className="shrink-0 text-primary">
                    <UserPlus2 className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">Invite "{trimmedQuery}"</p>
                    <p className="text-xs text-muted-foreground truncate">Press Enter or click to add</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-3 py-2.5 border-t border-border">
              <Button size="sm" variant="ghost" className="h-8 px-3 text-xs" onClick={cancelDropdown}>
                {t("common.cancel")}
              </Button>
              <Button size="sm" className="h-8 px-4 text-xs bg-primary hover:bg-primary/90 text-white" onClick={commitAdd}>
                {t("meetings.schedule.add")}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Selected Users List */}
      <div className="px-6 pb-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">
            • {tabLabel}
          </h3>
          <span className="text-xs text-muted-foreground">
            {activeTab === "active" ? selectedActiveCount : selectedPassiveCount}
          </span>
        </div>

        {(activeTab === "active" ? selectedActiveUsers : selectedPassiveUsers).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center mb-4">
            <img src={assetRoomsNoData} alt=""/>
            </div>
            <p className="text-sm text-muted-foreground">
              No {tabLabel} added yet
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(activeTab === "active" ? selectedActiveUsers : selectedPassiveUsers).map((user) => (
              <div
                key={user.email}
                className="flex items-center gap-3 p-3 bg-card border border-border rounded-xl"
              >
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                {/* Only the organiser is badged. This was hardcoded to "Host"
                    and rendered on every row of both lists, so everyone
                    invited was shown as the meeting's host — including
                    view-only attendees, who are the one group that certainly
                    cannot moderate anything. Everyone else needs no badge: the
                    tab they are listed under already says which audience they
                    belong to, and repeating it on each row said nothing the
                    heading had not. `isHostEmail` decides the remove control
                    a few lines down for the same reason. */}
                {isHostEmail(user.email) && (
                  <Badge className="bg-primary text-white text-xs px-2 py-1">
                    {t("meetings.schedule.roleHost")}
                  </Badge>
                )}
                {/* No remove control for the organiser — offering an action
                    that is then ignored is worse than not offering it. A
                    spacer keeps their row the same width as everyone else's.

                    It used to read `t("meetings.schedule.organizer")`, a key
                    that was never added: `t` answers a miss with the key
                    itself, so the row rendered the literal string
                    "meetings.schedule.organizer" — and the `|| "Organizer"`
                    fallback could never fire, because a key is not empty. The
                    Host badge beside it already says who they are, so there
                    was nothing for a second label to add even spelled right. */}
                {isHostEmail(user.email) ? (
                  <span className="w-8 h-8 shrink-0" aria-hidden />
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleContact(user.email)}
                    className="w-8 h-8 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-500/15 dark:hover:text-red-400"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  )
}

interface MeetingData {
  name: string
  description: string
  date: Date | undefined
  time: string
  duration: string
  timezone: string
  license: string
  seats: number
  recurring: boolean
  recurrence: RecurrenceState
  password: string
  passwordEnabled: boolean
  /** Two audiences: full participants and view-only attendees. */
  passiveParticipantsEnabled: boolean
  reminderEnabled: boolean
  reminderBeforeMinutes: number
  recording: boolean
  transcription: boolean
  anyTimeJoin: boolean
  telepresence: boolean
  videoOn: boolean
  muteOnEntry: boolean
  simulcast: boolean
  sipEnabled: boolean
  attendance: AttendanceConfig
  attachments: AttachmentFile[]
  invitees: string[]
  /** The split, for the payload only. `invitees` above stays the full
   *  selection because the panel renders from it. */
  passiveInvitees: string[]
  additionalTimezones: string[]
}

/** Per-license seat ceilings — mirrors the InstantMeetingDialog license list. */
const LICENSE_SEATS: Record<string, number> = {
  Invccp_30: 30,
  Invccp_50: 50,
  Invccp_100: 100,
}

const seatLimitFor = (license: string): number => LICENSE_SEATS[license] ?? 30

/**
 * The smallest licence that can seat this many.
 *
 * Needed because a seat count arriving from the Instant Meeting dialog can be
 * larger than the default tier holds: landing 50 seats on `Invccp_30` would
 * clamp straight back to 30 and lose the choice a second time.
 */
const licenseForSeats = (seats: number): string => {
  const fit = Object.entries(LICENSE_SEATS)
    .sort((a, b) => a[1] - b[1])
    .find(([, limit]) => limit >= seats)
  return fit?.[0] ?? "Invccp_100"
}

/** Keys a number input accepts that a seat count never has. */
const NOT_A_SEAT_KEY = new Set(["e", "E", "+", "-", ".", ","])

/**
 * The "No. of Seats" box, here and in InstantMeetingDialog. It can be emptied
 * and retyped (typedSeats / settledSeats), but only ever reports a valid count.
 */
export function SeatCountInput({ id, value, max, onChange, error }: {
  id?: string
  value: number
  max: number
  onChange: (seats: number) => void
  error?: boolean
}) {
  // What is in the box while it is being typed in; null shows `value`.
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <Input
      id={id}
      type="number"
      inputMode="numeric"
      min={MEETING_SEATS_MIN}
      max={max}
      step={1}
      value={draft ?? value}
      onKeyDown={(e) => {
        if (NOT_A_SEAT_KEY.has(e.key)) e.preventDefault()
      }}
      onChange={(e) => {
        const typed = typedSeats(e.target.value, max)
        if (!typed) return
        setDraft(typed.text)
        if (typed.seats !== undefined) onChange(typed.seats)
      }}
      onBlur={() => {
        if (draft === null) return
        onChange(settledSeats(draft, value, max))
        setDraft(null)
      }}
      className="w-16 flex text-center text-xl font-semibold border-slate-300 dark:border-slate-600"
      error={error}
    />
  )
}

export default function ScheduleMeeting() {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const fetchFromApi = useMeetingsStore((s) => s.fetchFromApi)
  const meetings = useMeetingsStore((s) => s.meetings)
  const user = useAuthStore((s) => s.user)
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const isEditMode = !!editId
  // For a day of a series: `all` edits every day that has not happened yet.
  const editAll = searchParams.get('scope') === 'all'

  // Carried over from the Instant Meeting dialog's "Advance" link, so what the
  // user set there survives the navigation instead of this screen falling back
  // to its own defaults.
  //
  // The name was the only field carried, so somebody who had raised the seat
  // count to 30 and then opened the advanced screen arrived to find it back at
  // 2 — the default below — and had to set it a second time.
  //
  // Seats travel as a NUMBER rather than as the dialog's licence, because the
  // two screens spell the same three tiers differently (`INVCCCP_30` there,
  // `Invccp_30` here). Passing the id across would silently miss every lookup;
  // passing the capacity lets this screen name the tier in its own vocabulary.
  const prefill = location.state as {
    prefillName?: string
    prefillSeats?: number
    prefillDuration?: string
  } | null
  const prefillName = prefill?.prefillName

  const meetingToEdit = useMemo(() => {
    if (!editId) return undefined
    return meetings.find((m) => m.id === editId)
  }, [editId, meetings])

  /**
   * May this user change capacity and the invitee list?
   *
   * Creating a meeting, always — you are its host. Editing one, only if the
   * server says so, and the list this page reads answers on ownership alone: a
   * co-host promoted during the meeting is a moderator whose id is nowhere on
   * the record. So the edit screen asks `GET /:id`, which resolves the live
   * role too, and falls back to what the list said if that call fails.
   *
   * Undefined while the answer is in flight — the controls stay hidden until it
   * arrives, which is the right way round: showing a field and then taking it
   * away is worse than showing it a moment late.
   */
  const [serverCanModerate, setServerCanModerate] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    if (!isEditMode || !editId) return
    let cancelled = false
    void (async () => {
      try {
        const { meeting } = await meetingsApi.getById(editId)
        if (!cancelled) setServerCanModerate(meeting?.canModerate === true)
      } catch {
        // Unreachable API: fall back to what the list said rather than locking
        // the host out of their own meeting.
        if (!cancelled) setServerCanModerate(undefined)
      }
    })()
    return () => { cancelled = true }
  }, [isEditMode, editId])

  /** Ownership, as the client can see it — the last-resort fallback. */
  const ownsMeetingToEdit = !!meetingToEdit && (
    (!!meetingToEdit.hostId && !!user?.id && meetingToEdit.hostId === user.id)
    || (!!meetingToEdit.hostEmail && !!user?.email
        && meetingToEdit.hostEmail.toLowerCase() === user.email.toLowerCase())
  )

  // Editing one day of a recurring series. Invitees, seats, password, the time
  // zone and every setting belong to the whole series, so only the day's own
  // fields (name, description, date, time, duration, files) are offered and
  // sent; the server refuses the rest for an occurrence.
  const editingOccurrence = isEditMode && !!meetingToEdit?.series && !editAll
  // "Edit all occurrences": the whole form, starting from the next meeting.
  const editingSeries = isEditMode && !!meetingToEdit?.series && editAll

  // The existing password, from the server, for the host editing it. The form
  // used to read it from this browser's local store only, so a meeting
  // scheduled through the server opened with an empty password field.
  const passwordWasOn = isEditMode && !!meetingToEdit?.settings?.passwordEnabled
  const existingPassword = useHostMeetingPassword(meetingToEdit?.id, passwordWasOn && ownsMeetingToEdit)
  useEffect(() => {
    if (!existingPassword) return
    // Only into an empty field: never over a password the host is typing.
    setMeetingData((prev) => (prev.password ? prev : { ...prev, password: existingPassword }))
  }, [existingPassword, meetingToEdit?.id])
  // The last password generatePassword filled in. The "Auto-generated" hint
  // shows only while the field still holds it: it stayed up after the field
  // was cleared or typed over, describing a password that was no longer there.
  const [generatedPassword, setGeneratedPassword] = useState("")

  const canManageCapacityAndInvitees = !editingOccurrence && (
    !isEditMode
    || serverCanModerate === true
    || (serverCanModerate === undefined
        && (meetingToEdit?.canModerate === true || ownsMeetingToEdit)))
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [timezonePlannerOpen, setTimezonePlannerOpen] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false)
  /**
   * Groups this meeting's attachments under one storage prefix.
   *
   * Minted here rather than using the meeting id, because the meeting does not
   * exist yet when files are attached. Stored on the meeting so the whole set
   * can be swept when it ends.
   */
  // Files the user removed that were already saved on the meeting. Held until
  // the save succeeds — see `removeAttachment`.
  const pendingAttachmentDeletes = useRef<{ url: string; id: string }[]>([])

  // One group per meeting, for the lifetime of the meeting — NOT one per visit
  // to this screen. Editing used to mint a fresh id and save it over the stored
  // one, so the files uploaded when the meeting was created were left under a
  // prefix nothing referenced any more: the end-of-meeting sweep, which deletes
  // by prefix, could never reach them.
  const [attachmentGroup] = useState(() => {
    const existing = (meetingToEdit?.settings as { attachmentGroup?: string } | undefined)
      ?.attachmentGroup
    if (existing) return existing
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now())
  })
  const [scheduleErrors, setScheduleErrors] = useState<Record<string, string>>({})

  const getDefaultFutureTime = () => {
    const future = new Date(Date.now() + 10 * 60_000)
    const snap = Math.ceil(future.getMinutes() / 5) * 5
    const overflow = snap >= 60
    const h = overflow ? (future.getHours() + 1) % 24 : future.getHours()
    const m = overflow ? 0 : snap
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  }

  // The organiser is always an invitee of their own meeting. Seeded here so the
  // UI shows them from the start and the payload carries them even if nobody
  // touches the invitee panel; the API adds them too, so the two agree.
  const hostEmail = useAuthStore.getState().user?.email ?? ''

  // email -> kind, published by the invitee panel. The split is computed from
  // this at submit rather than mirrored into state, so no other update can
  // revert it between the panel reporting and the form being sent.
  const [inviteeStatus, setInviteeStatus] = useState<Record<string, "active" | "passive">>({})

  const [meetingData, setMeetingData] = useState<MeetingData>({
    name: prefillName || (user ? `${user.name}'s Meeting` : "My Meeting"),
    description: "",
    date: new Date(),
    time: getDefaultFutureTime(),
    duration: prefill?.prefillDuration || "60",
    timezone: getDefaultTimezone(),
    license: prefill?.prefillSeats ? licenseForSeats(prefill.prefillSeats) : "Invccp_30",
    passiveParticipantsEnabled: false,
    seats: prefill?.prefillSeats ?? 2,
    invitees: hostEmail ? [hostEmail] : [],
    passiveInvitees: [],
    additionalTimezones: [],
    recurring: false,
    recurrence: defaultRecurrence(new Date()),
    password: "",
    passwordEnabled: false,
    reminderEnabled: false,
    reminderBeforeMinutes: 10,
    recording: false,
    transcription: false,
    anyTimeJoin: false,
    telepresence: false,
    videoOn: false,
    muteOnEntry: false,
    simulcast: true,
    sipEnabled: false,
    attendance: {
      enabled: false,
      requireApproval: false,
      allowLateJoin: true,
    },
    attachments: [],
  })

  useEffect(() => {
    if (!isEditMode) return
    if (!meetingToEdit) {
      toast.error({
        title: t('common.error'),
        description: t('meetings.errors.MEETING_NOT_FOUND'),
      })
      navigate('/meetings')
      return
    }

    // "Edit all occurrences" starts from the next meeting that has not happened,
    // and "End after N" counts the meetings left, so saving unchanged keeps
    // every date where it is.
    const series = editAll ? meetingToEdit.series : undefined
    if (series && !series.upcoming) {
      toast.error({ title: t('common.error'), description: t('meetings.errors.SERIES_ENDED') })
      navigate('/meetings')
      return
    }
    const recurrenceConfig = series
      ? {
          ...series.rule,
          occurrences: series.rule.endType === 'date' ? series.rule.occurrences : series.upcoming!.count,
        }
      : meetingToEdit.recurringConfig
    // Read the saved instant back out in the MEETING's own zone, not the
    // browser's: otherwise a meeting scheduled for 11:05 AM Kolkata by
    // someone in that zone shows up as 5:35 AM to an editor sitting in
    // London, and re-saving without touching the time silently moves it.
    const editTimezone = canonicalTimezone(meetingToEdit.timezone || getDefaultTimezone())
    const editParts = utcMsToZonedParts(series?.upcoming?.nextStartMs ?? meetingToEdit.startTimeMs, editTimezone)
    const d = new Date(editParts.year, editParts.month - 1, editParts.day)
    const hh = String(editParts.hour).padStart(2, '0')
    const mm = String(editParts.minute).padStart(2, '0')
    const settings = meetingToEdit.settings
    const credential = settings?.passwordEnabled
      ? meetingCredentialsRepo.get(meetingToEdit.id)
      : undefined

    setMeetingData((prev) => ({
      ...prev,
      name: meetingToEdit.title,
      description: meetingToEdit.description ?? '',
      date: d,
      time: `${hh}:${mm}`,
      duration: String(meetingToEdit.durationMinutes ?? meetingToEdit.duration ?? 60),
      seats: meetingToEdit.seats ?? prev.seats,
      timezone: meetingToEdit.timezone ? editTimezone : prev.timezone,
      // The saved meeting keeps them apart; the panel needs the full list to
      // render and the split for the payload, so rebuild all three.
      invitees: [
        ...(meetingToEdit.invitees ?? []),
        ...(meetingToEdit.passiveInvitees ?? []),
      ],
      passiveInvitees: meetingToEdit.passiveInvitees ?? prev.passiveInvitees,
      passwordEnabled: settings?.passwordEnabled ?? false,
      passiveParticipantsEnabled: settings?.passiveParticipantsEnabled ?? false,
      password: credential?.plaintext ?? '',
      recording: settings?.autoRecord ?? false,
      transcription: settings?.transcriptionEnabled ?? false,
      anyTimeJoin: settings?.allowJoinAnytime ?? false,
      // Files already on the meeting.
      //
      // Every other field was carried into edit mode and this one was not, so
      // opening a meeting to change it showed an empty attachments list — the
      // PDF that had been uploaded was there on the record and nowhere on the
      // screen, with no way to view or remove it. The save path a few hundred
      // lines down writes `meetingData.attachments` wholesale, so the list
      // starting empty did not merely hide them: saving any edit at all wrote
      // the empty list back and took the attachments with it.
      attachments: (meetingToEdit.attachments ?? []).map((a) => ({
        id: a.id,
        name: a.name,
        size: a.size,
        type: a.type,
        url: a.url,
        ...(a.previewUrl ? { previewUrl: a.previewUrl, previewId: a.previewId } : {}),
        saved: true,
      })),
      telepresence: settings?.telepresence ?? false,
      videoOn: settings?.videoOnEntry ?? false,
      muteOnEntry: settings?.muteOnEntry ?? false,
      simulcast: settings?.simulcast ?? true,
      sipEnabled: settings?.sipEnabled ?? false,
      reminderEnabled: settings?.reminderEnabled ?? false,
      reminderBeforeMinutes: settings?.reminderBeforeMinutes ?? 10,
      attendance: {
        enabled: (settings?.requireApproval ?? false) || settings?.waitingRoomEnabled === true,
        requireApproval: settings?.requireApproval ?? false,
        allowLateJoin: settings?.allowLateJoin ?? true,
      },
      recurring: series ? true : (meetingToEdit.recurring ?? false),
      recurrence: recurrenceFromConfig(recurrenceConfig, d),
    }))
  }, [isEditMode, editAll, meetingToEdit, navigate, t])

  const reminderOptions = [
    { label: t("meetings.schedule.reminder5Min"), value: 5 },
    { label: t("meetings.schedule.reminder10Min"), value: 10 },
    { label: t("meetings.schedule.reminder15Min"), value: 15 },
    { label: t("meetings.schedule.reminder30Min"), value: 30 },
    { label: t("meetings.schedule.reminder1Hour"), value: 60 },
    { label: t("meetings.schedule.reminder2Hours"), value: 120 },
    { label: t("meetings.schedule.reminder1Day"), value: 1440 },
    { label: t("meetings.schedule.reminder2Days"), value: 2880 },
  ]

  // From the latest state: switching the password on calls this in the same
  // handler that sets passwordEnabled, and spreading the render's copy here
  // wrote passwordEnabled back to false — the switch took two clicks.
  const generatePassword = () => {
    const password = serviceGeneratePassword()
    setGeneratedPassword(password)
    setMeetingData((prev) => ({ ...prev, password }))
  }

  /**
   * Upload each picked file, then record it on the meeting.
   *
   * This used to keep the name and size and nothing else — the bytes were never
   * sent anywhere, so an "attached" document existed only in the form and was
   * gone the moment it was submitted. Uploading here puts it in the same store
   * meeting shares use, so the meeting can hand every participant a URL that
   * resolves.
   *
   * 25 MB, not 50: that is the backend's own limit, and the larger number only
   * meant the upload failed later instead of the file being refused now.
   *
   * Refusals are counted by `partitionPickedAttachments` and announced once per
   * REASON rather than once per file. Naming the file was the complaint: the
   * message states a rule printed above the button, and a refused file never
   * reaches the list below, so there is nothing on screen for a name to pick out.
   */
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return

    const { accepted, rejectedType, rejectedSize } = partitionPickedAttachments(
      Array.from(files),
      meetingData.attachments,
      {
        slotsLeft: MAX_ATTACHMENTS - meetingData.attachments.length,
        maxBytes: MAX_SHARE_UPLOAD_BYTES,
      },
    )

    // Title only, no description — the rule IS the message.
    if (rejectedType > 0) toast.error({ title: t("meetings.schedule.pdfOnly") })
    if (rejectedSize > 0) toast.error({ title: t("meetings.schedule.fileTooLarge") })

    setIsUploadingAttachment(true)

    for (const file of accepted) {
      try {
        const uploaded = await uploadShareFile(file, attachmentGroup)
        setMeetingData(prev => ({
          ...prev,
          attachments: [
            ...prev.attachments,
            {
              // The server's attachment id, not a random one. This is the only
              // handle that can delete the object later: on S3 the URL is a
              // presigned bucket link with no id in it, so a locally invented
              // id left the file unreachable and it stayed in the bucket after
              // being removed from the meeting.
              id: uploaded.id,
              name: uploaded.name,
              size: uploaded.size,
              type: uploaded.mime,
              url: uploaded.url,
              ...(uploaded.preview
                ? { previewUrl: uploaded.preview.url, previewId: uploaded.preview.id }
                : {}),
            },
          ],
        }))
      } catch (err) {
        toast.error({
          title: `Could not attach ${file.name}`,
          description: err instanceof Error ? err.message : "Upload failed.",
        })
      }
    }

    setIsUploadingAttachment(false)
    // Let the same file be picked again after a failure.
    event.target.value = ""
  }

  const removeAttachment = (id: string) => {
    const removed = meetingData.attachments.find(f => f.id === id)
    if (removed?.url) {
      // `removed.id` is the server's attachment id for anything uploaded since
      // this fix; older records carry a random one, and `deleteUploadedShare`
      // falls back to the URL for those — which only resolves on local disk.
      if (removed.saved) {
        // Already on the meeting: the file goes when the edit is SAVED, not
        // when the row is removed. Deleting here would destroy it even if the
        // user backed out, leaving the meeting pointing at an object that is
        // no longer in the bucket.
        pendingAttachmentDeletes.current.push({ url: removed.url, id: removed.id })
        if (removed.previewUrl) {
          pendingAttachmentDeletes.current.push({ url: removed.previewUrl, id: removed.previewId ?? '' })
        }
      } else {
        // Uploaded during this edit. It was uploaded the moment it was picked,
        // so dropping the row on its own would leave the object belonging to
        // nothing, and there is nothing to back out to.
        void deleteUploadedShare(removed.url, removed.id)
        if (removed.previewUrl) void deleteUploadedShare(removed.previewUrl, removed.previewId)
      }
    }
    setMeetingData(prev => ({ ...prev, attachments: prev.attachments.filter(f => f.id !== id) }))
  }

  const removeAdditionalTimezone = (tz: string) => {
    setMeetingData(prev => ({ ...prev, additionalTimezones: prev.additionalTimezones.filter(z => z !== tz) }))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  /** Map a validator field name onto the local schedule form field key. */
  const mapServiceErrors = (errors: Record<string, string>): Record<string, string> => {
    const mapped: Record<string, string> = {}
    if (errors.title) mapped.name = errors.title
    if (errors.time) mapped.time = errors.time
    if (errors.date) mapped.time = errors.date
    if (errors.duration) mapped.duration = errors.duration
    if (errors.password) mapped.password = errors.password
    if (errors.recurringWeekDays) mapped.recurrence = errors.recurringWeekDays
    if (errors.recurringOccurrences) mapped.recurrence = errors.recurringOccurrences
    if (errors.recurringEndDate) mapped.recurrence = errors.recurringEndDate
    return mapped
  }

  const buildSettings = (): Partial<MeetingSettings> => ({
    passwordEnabled: meetingData.passwordEnabled,
    waitingRoomEnabled: meetingData.attendance.enabled && meetingData.attendance.requireApproval,
    lockAfterStart: false,
    muteOnEntry: meetingData.muteOnEntry,
    videoOnEntry: meetingData.videoOn,
    disableParticipantCamera: false,
    allowScreenShare: 'all',
    allowChat: true,
    allowReactions: true,
    allowParticipantRename: true,
    allowParticipantUnmute: true,
    autoRecord: meetingData.recording,
    transcriptionEnabled: meetingData.transcription,
    allowJoinAnytime: meetingData.anyTimeJoin,
    allowLateJoin: meetingData.attendance.allowLateJoin,
    requireApproval: meetingData.attendance.requireApproval,
    passiveParticipantsEnabled: meetingData.passiveParticipantsEnabled,
    reminderEnabled: meetingData.reminderEnabled,
    reminderBeforeMinutes: meetingData.reminderBeforeMinutes,
    simulcast: meetingData.simulcast,
    sipEnabled: meetingData.sipEnabled,
    telepresence: meetingData.telepresence,
  })

  const handleSchedule = async () => {
    setScheduleErrors({})

    // Check the name here, before the request.
    //
    // This screen posts its payload straight to the API and never ran the
    // validators the local service uses, so two things went wrong at once. A
    // name of nothing but spaces passed straight through and a meeting was
    // created with a blank title. And when the server did refuse something,
    // the only place that said so was a toast — the field the problem was in
    // said nothing, so there was no way to tell WHICH field, or what about it
    // was wrong.
    const nameErrors: Record<string, string> = {}
    validateTitle(meetingData.name, nameErrors, { field: 'name' })
    if (nameErrors.name) {
      setScheduleErrors(nameErrors)
      return
    }

    // More people than seats.
    //
    // The panel refuses an invitation that would not fit, so the only way to
    // arrive here is from the other end: inviting eight and then winding the
    // seat count down to four. The message belongs on the seats field, which
    // is the half of the pair that moved and the half worth changing back.
    if (canManageCapacityAndInvitees && meetingData.invitees.length > meetingData.seats) {
      setScheduleErrors({
        seats: t('meetings.schedule.tooManyInvitees', {
          wanted: meetingData.invitees.length,
          seats: meetingData.seats,
        }),
      })
      return
    }

    const recurringConfig = meetingData.recurring
      ? recurrenceToConfig(meetingData.recurrence)
      : undefined

    // The picker already shows this inline, but it sits inside a collapsible
    // section — open it so the message is on screen, not behind a chevron.
    const endProblem = recurrenceEndDateProblem(recurringConfig, meetingData.date)
    if (endProblem) {
      setShowAdvanced(true)
      setScheduleErrors({ recurrence: t(END_DATE_PROBLEM_KEY[endProblem]) })
      return
    }

    setIsScheduling(true)
    try {
      const settings = buildSettings()

      const durationMinutes = parseInt(meetingData.duration, 10)
      const startMs = resolveStartMs({ date: meetingData.date, time: meetingData.time, timeZone: meetingData.timezone })!
      const endMs = startMs + durationMinutes * 60_000
      // Capacity and invitees are omitted entirely when this user may not change
      // them, rather than sent unchanged. The server refuses the whole update if
      // either field is present without the permission, so echoing back what was
      // loaded would fail every non-moderator edit — and "send it, it's the same
      // value" is exactly the assumption that stops being true the moment
      // somebody else edits the meeting in another tab.
      const moderatorFields = canManageCapacityAndInvitees
        ? {
            // `invitees` are registered users, `passiveinvities` those invited
            // by email alone. The organiser is always among the former. Split
            // here, from the one list the panel maintains plus the kinds it
            // reported. Anything unknown counts as active — the safer default,
            // and what an address with no recorded kind has always been.
            invitees: Array.from(
              new Set([
                ...(hostEmail ? [hostEmail] : []),
                ...meetingData.invitees.filter(
                  (e) => inviteeStatus[e.toLowerCase()] !== 'passive',
                ),
              ]),
            ),
            passiveinvities: meetingData.invitees.filter(
              (e) => inviteeStatus[e.toLowerCase()] === 'passive',
            ),
            seats: meetingData.seats,
          }
        : {}

      const occurrencePayload = {
        title: meetingData.name.trim(),
        // Sent even when emptied, so clearing one day's description sticks.
        description: meetingData.description,
        start_time: new Date(startMs).toISOString(),
        end_time: new Date(endMs).toISOString(),
        duration_minutes: durationMinutes,
        attachments: meetingData.attachments.filter(a => !!a.url),
      }

      const apiPayload = editingOccurrence ? occurrencePayload : {
        // Trimmed: what the validator judged is what gets stored.
        title: meetingData.name.trim(),
        description: meetingData.description || undefined,
        start_time: new Date(startMs).toISOString(),
        end_time: new Date(endMs).toISOString(),
        duration_minutes: durationMinutes,
        timezone: meetingData.timezone,
        ...moderatorFields,
        recurring: meetingData.recurring,
        recurring_config: recurringConfig,
        password: meetingData.passwordEnabled ? meetingData.password : undefined,
        settings,
        simulcast_enabled: meetingData.simulcast,
        // Only uploaded ones — an entry without a URL is a file that never
        // reached the server and would be a dead row in the meeting.
        attachments: meetingData.attachments.filter(a => !!a.url),
        // Kept with the meeting so its files can be swept together later.
        attachment_group: attachmentGroup,
      }

      if (isEditMode && editId) {
        await meetingsApi.update(editId, apiPayload, editingSeries ? { scope: 'all' } : {})
        // The edit stuck, so the files the user removed can go for good. Only
        // now — doing it at the moment of removal would have destroyed them
        // even if the user then backed out of the edit.
        for (const a of pendingAttachmentDeletes.current) void deleteUploadedShare(a.url, a.id)
        pendingAttachmentDeletes.current = []
      } else {
        await meetingsApi.schedule(apiPayload)
      }

      void fetchFromApi()
      toast.success({
        title: isEditMode
          ? t('common.updated')
          : t('meetings.schedule.meetingScheduled'),
        description: isEditMode
          ? t('meetings.meetingUpdated')
          : t('meetings.schedule.meetingScheduledDesc'),
      })
      navigate('/meetings')
    } catch (err) {
      if (isValidationError(err)) {
        setScheduleErrors(mapServiceErrors(err.errors))
      } else if (isMeetingError(err)) {
        // `t` hands back the key path when a code has no copy, so an error the
        // server added and nobody wrote a line for was shown to the user as
        // "meetings.errors.SOMETHING" — which reads as a fault in the app and
        // says nothing about what to do. The server's own message is at least
        // about the problem.
        const key = `meetings.errors.${err.code}`
        const copy = t(key)
        toast.error({
          title: t('meetings.couldNotSaveMeeting'),
          description: copy === key ? err.message : copy,
        })
      } else {
        toast.error({ title: t('meetings.couldNotSaveMeeting') })
      }
    } finally {
      setIsScheduling(false)
    }
  }

  const handleCancel = () => {
    navigate("/meetings")
  }

  return (
    <>
    {isScheduling && <FullPageDotsLoader message={t("meetings.schedule.schedulingMeeting")} />}
    <div className="h-full w-full overflow-hidden">
      <div className="h-full flex flex-col lg:flex-row gap-4 lg:gap-6 px-2 sm:px-6 lg:px-8 py-4">
        {/* Left Side - Schedule Form */}
        <div className="flex-1 flex flex-col gap-4 lg:gap-6 overflow-y-auto">
          {/* Header */}
          <div className="flex items-center gap-4 flex-shrink-0">
            <Button
              variant="ghost"
              onClick={handleCancel}
              className="h-10 w-10 rounded-full hover:bg-slate-200 dark:bg-card dark:hover:bg-muted bg-secondary-100 transition-all"
            >
              <ChevronDown className="w-5 h-5 rotate-90" />
            </Button>
            <div className="flex flex-col">
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 dark:from-white dark:to-slate-300 bg-clip-text text-transparent">
                {editingOccurrence && meetingToEdit
                  ? t("meetings.series.editOccurrenceTitle", {
                      date: formatOccurrenceDay(meetingToEdit.series?.originalStartMs ?? meetingToEdit.startTimeMs, language),
                    })
                  : editingSeries
                    ? t("meetings.series.editAllTitle")
                    : t("meetings.schedule.title")}
              </h1>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {editingOccurrence
                  ? t("meetings.series.editOccurrenceSubtitle")
                  : editingSeries
                    ? t("meetings.series.editAllSubtitle")
                    : t("meetings.schedule.subtitle")}
              </p>
            </div>
          </div>
          
          {/* Form Card */}
          <Card className="flex-1 flex flex-col rounded-3xl border border-stroke-100 dark:border-border bg-card backdrop-blur">
            <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 sm:py-6 space-y-6">
            {/* Meeting Name */}
            <div className="space-y-2">
              <Label htmlFor="meeting-name" className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2 block flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                {t("meetings.schedule.meetingName")}
              </Label>
              <Input
                id="meeting-name"
                value={meetingData.name}
                onChange={(e) => { setMeetingData({ ...meetingData, name: e.target.value.slice(0, 50) }); setScheduleErrors(p => ({ ...p, name: '' })) }}
                placeholder={t("meetings.schedule.enterMeetingName")}
                className="text-sm"
                maxLength={50}
                error={!!scheduleErrors.name}
                errorText={scheduleErrors.name}
              />
              <div className={`text-[11px] text-right ${meetingData.name.length >= 50 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {meetingData.name.length}/50
              </div>
            </div>
              {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="meeting-description" className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2 block flex items-center gap-2">
                <Zap className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                {t("meetings.schedule.description")}
              </Label>
              <Textarea
                id="meeting-description"
                value={meetingData.description}
                onChange={(e) => setMeetingData({ ...meetingData, description: e.target.value.slice(0, 500) })}
                placeholder={t("meetings.schedule.addDescription")}
                rows={3}
                maxLength={500}
                className="resize-none border-border dark:bg-card/30 focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
              <div className={`text-[11px] text-right ${meetingData.description.length >= 500 ? 'text-destructive' : 'text-muted-foreground'}`}>
                {meetingData.description.length}/500
              </div>
            </div>

            {/* Select License — capacity is moderator-only, so the licence that
                bounds it in the UI is hidden with it. */}
            {canManageCapacityAndInvitees && (
            <div className="space-y-2">
              <Label htmlFor="meeting-license" className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2 block flex items-center gap-2">
                <Shield className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                {t("meetings.schedule.selectLicense")}
              </Label>
              <Select
                value={meetingData.license}
                onValueChange={(value) => {
                  const max = seatLimitFor(value)
                  setMeetingData({
                    ...meetingData,
                    license: value,
                    seats: Math.min(meetingData.seats, max),
                  })
                }}
              >
                <SelectTrigger id="meeting-license" >
                  <SelectValue placeholder={t("meetings.schedule.selectLicensePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Invccp_30">Invccp_30 (30 seats)</SelectItem>
                  <SelectItem value="Invccp_50">Invccp_50 (50 seats)</SelectItem>
                  <SelectItem value="Invccp_100">Invccp_100 (100 seats)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}

            {canManageCapacityAndInvitees && (
            <>
            {/* No. of Seats — label/input on one row (label left, input right)
                like InstantMeetingDialog. Upper bound tracks the selected
                license; the service rejects values outside the allowed range. */}
            <div className="flex flex-col gap-1">
              <div className="flex w-full items-center justify-between">
                <span className="flex flex-1 items-center gap-2 text-sm font-medium text-slate-900 dark:text-foreground/80 dark:text-foreground">
                  <Users className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                  {t("meetings.noOfSeats")} ({seatLimitFor(meetingData.license)} {t("meetings.seatsAvailable")})
                </span>
                <div>
                <SeatCountInput
                  id="meeting-seats"
                  max={seatLimitFor(meetingData.license)}
                  value={meetingData.seats}
                  onChange={(seats) => {
                    setMeetingData((prev) => ({ ...prev, seats }))
                    setScheduleErrors((p) => ({ ...p, seats: "" }))
                  }}
                  error={!!scheduleErrors.seats}
                />
                </div>
              </div>
              {scheduleErrors.seats && (
                <p className="text-xs text-error-600 dark:text-error-500">{scheduleErrors.seats}</p>
              )}
            </div>
            </>
            )}

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <CalendarIcon className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                  {t("meetings.schedule.date")}
                </Label>
                <DatePicker
                  date={meetingData.date}
                  // Yesterday is not a meeting you can schedule. The submit
                  // check has always said so; the calendar used to offer it
                  // anyway and only argue once everything else was filled in.
                  // "Yesterday" is judged in the selected zone, not the
                  // browser's, so a date that is still today in, say,
                  // Kolkata is not greyed out just because it has already
                  // rolled past midnight where the browser sits.
                  minDate={zonedNow(meetingData.timezone)}
                  setDate={(date) => {
                    const floor = earliestTimeFor(date, zonedNow(meetingData.timezone), TIME_STEP)
                    setMeetingData((prev) => ({
                      ...prev,
                      date,
                      // Coming back to today can leave the time already chosen
                      // in the past — 4pm is fine for tomorrow and gone by
                      // this evening. Moved forward rather than cleared, so
                      // the form stays filled in.
                      time: isBefore(prev.time, floor) ? floor ?? prev.time : prev.time,
                      // The default weekday / day of month belong to the start
                      // date, so they move with it until picked by hand.
                      recurrence: date ? followStartDate(prev.recurrence, date) : prev.recurrence,
                    }))
                    setScheduleErrors((p) => ({ ...p, time: "", recurrence: "" }))
                  }}
                  placeholder={t("meetings.schedule.selectDate")}
                  variant="yearMonth"
                  className="h-10 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <ClockIcon className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                  {t("meetings.schedule.time")}
                </Label>
                {/* Shadcn-style TimePicker — popover with hour/minute columns
                    so users can pick any time at 5-minute granularity. */}
                <TimePicker
                  id="meeting-time"
                  value={meetingData.time}
                  step={TIME_STEP}
                  // Only ever a floor for TODAY: every other day is open from
                  // midnight. Read at render rather than held in state, so it
                  // keeps up with a form somebody left open.
                  min={earliestTimeFor(meetingData.date, zonedNow(meetingData.timezone), TIME_STEP) ?? undefined}
                  onChange={(v) => {
                    setMeetingData({ ...meetingData, time: v })
                    setScheduleErrors((p) => ({ ...p, time: "" }))
                  }}
                  placeholder={t("meetings.schedule.selectTime")}
                  aria-label={t("meetings.schedule.time")}
                  error={!!scheduleErrors.time}
                />
                {scheduleErrors.time && (
                  <p className="text-xs text-error-600 dark:text-error-500">{scheduleErrors.time}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="meeting-duration" className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2 block">
                  {t("meetings.schedule.duration")}
                </Label>
                <Select
                  value={meetingData.duration}
                  onValueChange={(value) => { setMeetingData({ ...meetingData, duration: value }); setScheduleErrors((p) => ({ ...p, duration: "" })) }}
                >
                  <SelectTrigger id="meeting-duration">
                    <SelectValue placeholder={t("meetings.schedule.selectDuration")} />
                  </SelectTrigger>
                  <SelectContent>
                    {[15, 30, 45, 60, 90, 120, 180, 240, 300, 360, 420, 480].map((minutes) => (
                      <SelectItem key={minutes} value={String(minutes)}>
                        {minutes >= 60 ? `${minutes / 60}${minutes === 60 ? ` ${t("meetings.schedule.hour")}` : ` ${t("meetings.schedule.hrs")}`}` : `${minutes} ${t("meetings.schedule.mins")}`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {scheduleErrors.duration && (
                  <p className="text-xs text-error-600 dark:text-error-500">{scheduleErrors.duration}</p>
                )}
              </div>
            </div>

            {/* Timezone: the series' own, so read-only when editing one day */}
            <fieldset disabled={editingOccurrence} className="space-y-2 min-w-0 border-0 p-0 m-0 disabled:opacity-60">
              <Label htmlFor="meeting-timezone" className="text-sm font-semibold text-slate-900 dark:text-foreground/80 dark:text-white mb-2 block flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-900 dark:text-foreground/80 dark:text-foreground" />
                {t("meetings.schedule.timezone")}
              </Label>
              <TimezoneSelect
                id="meeting-timezone"
                value={meetingData.timezone}
                onChange={(value) => setMeetingData({ ...meetingData, timezone: value })}
                placeholder={t("meetings.schedule.selectTimezone")}
                hideIcon
              />
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTimezonePlannerOpen(true)}
                  className="gap-1.5 text-xs h-8 px-3"
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {t("meetings.schedule.addTimeZonePlanner")}
                </Button>
              </div>

              {/* Additional Timezones: removable pills with a clock icon, so
                  they read as applied time zones and not as attachment rows. */}
              {meetingData.additionalTimezones.length > 0 && (
                <div className="mt-3">
                  <div className="flex flex-wrap gap-2">
                    {meetingData.additionalTimezones.map((tz) => (
                      <span
                        key={tz}
                        className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary dark:text-primary-400 dark:border-primary/30 text-xs font-medium"
                      >
                        <ClockIcon className="w-3 h-3" />
                        {formatTimezoneLabel(tz)}
                        <button
                          type="button"
                          onClick={() => removeAdditionalTimezone(tz)}
                          aria-label={t("common.remove")}
                          title={t("common.remove")}
                          className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center hover:bg-primary/20 transition-colors"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {editingOccurrence && (
                <p className="text-xs text-muted-foreground">{t("meetings.series.appliesToSeries")}</p>
              )}
            </fieldset>

          

            {/* Attachments */}
            <div className="space-y-3">
              {/* The upload control first, its rules directly under it: the
                  limits explain what the button accepts, so they read after
                  it rather than as a heading above it. */}
              <div className="space-y-1.5">
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept={ATTACHABLE_ACCEPT}
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById("file-upload")?.click()}
                  disabled={meetingData.attachments.length >= MAX_ATTACHMENTS}
                  aria-describedby="attachments-rules"
                  className="gap-2"
                >
                  <Upload className="w-4 h-4" />
                  {t("meetings.schedule.uploadFiles")}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {meetingData.attachments.length}/{MAX_ATTACHMENTS} {t("meetings.schedule.filesLimit")}
                </span>
              </div>
              <p id="attachments-rules" className="text-xs text-muted-foreground">
                {t("meetings.schedule.attachmentsLabel")}
              </p>
              </div>
              {meetingData.attachments.length > 0 && (
                <div className="space-y-2">
                  {meetingData.attachments.map((file) => (
                    <div key={file.id} className="flex items-center justify-between p-2 border border-border rounded-lg bg-muted/10">
                      <div className="flex items-center gap-3">
                        <AttachmentIcon name={file.name} type={file.type} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{formatFileSize(file.size)}</p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(file.id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:text-red-400 dark:hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Invitees - Mobile Only. Adding one sends mail in this meeting's
                name, so the panel belongs to moderators. */}
            {canManageCapacityAndInvitees && (
            <div className="lg:hidden space-y-4">
              <div className="flex items-center gap-2.5">
                <Users className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{t("meetings.schedule.invitees")}</span>
              </div>
              <InvitesPanel 
                selectedContacts={meetingData.invitees}
                hostEmail={hostEmail}
                initialPassive={meetingData.passiveInvitees}
                passiveEnabled={meetingData.passiveParticipantsEnabled}
                seats={meetingData.seats}
                onStatusesChange={(m) => setInviteeStatus((prev) => ({ ...prev, ...m }))}
                onContactsChange={(contacts) => setMeetingData((prev) => ({ ...prev, invitees: contacts }))}
              />
            </div>
            )}

            {/* Advanced Options. For one occurrence, the series they belong to
                instead: every one of them is shared by the whole series. */}
            {editingOccurrence && meetingToEdit?.series ? (
              <div className="rounded-xl bg-muted/40 p-3 sm:p-4 space-y-1.5">
                <div className="flex items-center gap-2.5">
                  <RefreshCw className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{t("meetings.schedule.recurringMeeting")}</span>
                </div>
                <p className="text-sm text-foreground pl-6">
                  {describeRecurrence(
                    meetingToEdit.series.rule,
                    new Date(meetingToEdit.series.startsAtMs),
                    language,
                    t,
                  )}
                </p>
                <p className="text-xs text-muted-foreground pl-6">{t("meetings.series.settingsApplyToSeries")}</p>
              </div>
            ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between py-2"
              >
                <div className="flex items-center gap-2.5">
                  <Settings className="w-4 h-4 text-primary" />
                  <span className="text-sm font-semibold text-foreground">{t("meetings.schedule.advancedOptions")}</span>
                  <span className="text-xs hidden sm:block text-muted-foreground">{t("meetings.schedule.securityRecordingRecurrenceMore")}</span>
                </div>
                {showAdvanced
                  ? <ChevronUp className="w-4 h-4 text-muted-foreground shrink-0" />
                  : <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />}
              </button>

              {showAdvanced && (
                <div className="space-y-2">

                  {/* Passive (view-only) attendees. The hint wraps onto its own
                      line inside the row, so it highlights with it. */}
                  <div className={`${OPTION_ROW} flex-wrap gap-y-1`}>
                    <span className="flex items-center gap-2.5">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">
                        {t("meetings.schedule.passiveParticipants")}
                      </span>
                    </span>
                    <Switch
                      checked={meetingData.passiveParticipantsEnabled}
                      // Named by the title alone; the hint is its description.
                      aria-label={t("meetings.schedule.passiveParticipants")}
                      aria-describedby="passive-participants-hint"
                      onCheckedChange={checked =>
                        setMeetingData({ ...meetingData, passiveParticipantsEnabled: checked })
                      }
                    />
                    <span id="passive-participants-hint" className="basis-full text-xs text-muted-foreground pl-6">
                      {meetingData.passiveParticipantsEnabled
                        ? t("meetings.schedule.passiveParticipantsOnHint")
                        : t("meetings.schedule.passiveParticipantsOffHint")}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    <div className={OPTION_ROW}>
                      <span className="flex items-center gap-2.5">
                        <ClockIcon className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{t("meetings.schedule.reminder")}</span>
                      </span>
                      <Switch
                        checked={meetingData.reminderEnabled}
                        onCheckedChange={checked => setMeetingData({ ...meetingData, reminderEnabled: checked })}
                      />
                    </div>
                    {meetingData.reminderEnabled && (
                      <div className="pl-0 sm:pl-6 pt-1">
                        <div className="rounded-xl bg-muted/40 p-2 sm:p-3">
                          <div className="flex flex-wrap gap-2">
                            {reminderOptions.map((option) => {
                              const active = meetingData.reminderBeforeMinutes === option.value
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  onClick={() => setMeetingData({ ...meetingData, reminderBeforeMinutes: option.value })}
                                  className={`rounded-full border px-3 py-1.5 text-xs sm:text-sm transition-colors ${
                                    active
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-border bg-card text-foreground hover:border-primary/40"
                                  }`}
                                >
                                  {option.label}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recurring */}
                  <div className="space-y-1.5">
                    <div className={editingSeries ? OPTION_ROW_LOCKED : OPTION_ROW}>
                      <span className="flex items-center gap-2.5">
                        <RefreshCw className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{t("meetings.schedule.recurringMeeting")}</span>
                      </span>
                      <Switch
                        checked={meetingData.recurring}
                        aria-label={t("meetings.schedule.recurringMeeting")}
                        // A series stays a series; deleting all occurrences is how it stops.
                        disabled={editingSeries}
                        aria-describedby={editingSeries ? "recurring-locked" : undefined}
                        onCheckedChange={checked => setMeetingData({ ...meetingData, recurring: checked })}
                      />
                    </div>
                    {editingSeries && (
                      <p id="recurring-locked" className="text-xs text-muted-foreground sm:pl-6">
                        {t("meetings.series.recurringLocked")}
                      </p>
                    )}
                    {meetingData.recurring && (
                      <div className="pl-0 sm:pl-6 pt-1">
                        <RecurrencePicker
                          value={meetingData.recurrence}
                          startDate={meetingData.date ?? new Date()}
                          error={scheduleErrors.recurrence || undefined}
                          onChange={(recurrence) => {
                            setMeetingData((prev) => ({ ...prev, recurrence }))
                            setScheduleErrors((p) => ({ ...p, recurrence: "" }))
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Password */}
                  <div className="space-y-1.5">
                    <div className={OPTION_ROW}>
                      <span className="flex items-center gap-2.5">
                        <Shield className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{t("meetings.schedule.meetingPassword")}</span>
                      </span>
                      <Switch
                        checked={meetingData.passwordEnabled}
                        onCheckedChange={checked => {
                          setMeetingData((prev) => ({ ...prev, passwordEnabled: checked }))
                          if (checked && !meetingData.password) generatePassword()
                        }}
                      />
                    </div>
                    {meetingData.passwordEnabled && (
                      <div className="flex gap-2 pl-6">
                        <Input
                          value={meetingData.password}
                          onChange={e => { setMeetingData({ ...meetingData, password: e.target.value }); setScheduleErrors((p) => ({ ...p, password: "" })) }}
                          // A password is set but no copy could be shown: an
                          // empty field keeps it, typing replaces it.
                          placeholder={passwordWasOn && !existingPassword
                            ? t("meetings.schedule.passwordSetPlaceholder")
                            : t("meetings.schedule.enterPassword")}
                          maxLength={MEETING_PASSWORD_MAX_LENGTH}
                          className="flex-1 h-9 text-sm"
                        />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={generatePassword}
                              aria-label={t("meetings.schedule.generateNewPassword")}
                              className="h-9 w-9 shrink-0"
                            >
                              <Sparkles className="w-4 h-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">{t("meetings.schedule.generateNewPassword")}</TooltipContent>
                        </Tooltip>
                      </div>
                    )}
                    {meetingData.passwordEnabled && scheduleErrors.password && (
                      <p className="text-xs text-error-600 dark:text-error-500 pl-6">{scheduleErrors.password}</p>
                    )}
                    {/* What switching the password on generated, and how it is
                        used. Shown, not hover-only: a tooltip never opens on touch.
                        Gone once the field no longer holds the generated
                        password (cleared, typed over, or a saved one being
                        edited): it would describe a password that is not there. */}
                    {meetingData.passwordEnabled && meetingData.password && meetingData.password === generatedPassword && (
                      <p className="flex items-start gap-1.5 text-xs text-muted-foreground pl-6">
                        <Info className="w-3.5 h-3.5 mt-px shrink-0" aria-hidden="true" />
                        <span>
                          {t("meetings.schedule.autoPasswordHint", {
                            length: MEETING_PASSWORD_LENGTH,
                            min: MEETING_PASSWORD_MIN_LENGTH,
                            max: MEETING_PASSWORD_MAX_LENGTH,
                          })}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Cloud Recording */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.cloudRecording")}</span>
                    </span>
                    <Switch
                      checked={meetingData.recording}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, recording: checked })}
                    />
                  </div>

                  {/* AI Transcription */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Mic className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.aiTranscription")}</span>
                    </span>
                    <Switch
                      checked={meetingData.transcription}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, transcription: checked })}
                    />
                  </div>

                  {/* Telepresence */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Monitor className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.telepresence")}</span>
                    </span>
                    <Switch
                      checked={meetingData.telepresence}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, telepresence: checked })}
                    />
                  </div>

                  {/* SIP Enable */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.enableSip")}</span>
                    </span>
                    <Switch
                      checked={meetingData.sipEnabled}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, sipEnabled: checked })}
                    />
                  </div>

                  {/* Attendance Tracking */}
                  <div className="space-y-1.5">
                    <div className={OPTION_ROW}>
                      <span className="flex items-center gap-2.5">
                        <UserPlus2 className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">{t("meetings.schedule.attendanceTracking")}</span>
                      </span>
                      <Switch
                        checked={meetingData.attendance.enabled}
                        onCheckedChange={checked => setMeetingData({ ...meetingData, attendance: { ...meetingData.attendance, enabled: checked } })}
                      />
                    </div>
                    {meetingData.attendance.enabled && (
                      <div className="pl-6 space-y-0.5">
                        <div className={OPTION_ROW}>
                          <span className="text-xs text-muted-foreground">{t("meetings.schedule.requireApprovalForAttendance")}</span>
                          <Switch
                            checked={meetingData.attendance.requireApproval}
                            onCheckedChange={checked => setMeetingData({ ...meetingData, attendance: { ...meetingData.attendance, requireApproval: checked } })}
                          />
                        </div>
                        <div className={OPTION_ROW}>
                          <span className="text-xs text-muted-foreground">{t("meetings.schedule.allowLateJoin")}</span>
                          <Switch
                            checked={meetingData.attendance.allowLateJoin}
                            onCheckedChange={checked => setMeetingData({ ...meetingData, attendance: { ...meetingData.attendance, allowLateJoin: checked } })}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Allow Join Anytime */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Users className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.allowParticipantsJoinAnytime")}</span>
                    </span>
                    <Switch
                      checked={meetingData.anyTimeJoin}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, anyTimeJoin: checked })}
                    />
                  </div>

                  {/* Video On by Default */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Video className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.videoOnByDefault")}</span>
                    </span>
                    <Switch
                      checked={meetingData.videoOn}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, videoOn: checked })}
                    />
                  </div>
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Mic className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.muteMicOnEntry")}</span>
                    </span>
                    <Switch
                      checked={meetingData.muteOnEntry}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, muteOnEntry: checked })}
                    />
                  </div>

                  {/* Enable Simulcast */}
                  <div className={OPTION_ROW}>
                    <span className="flex items-center gap-2.5">
                      <Zap className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">{t("meetings.schedule.enableSimulcast")}</span>
                    </span>
                    <Switch
                      checked={meetingData.simulcast}
                      onCheckedChange={checked => setMeetingData({ ...meetingData, simulcast: checked })}
                    />
                  </div>

                </div>
              )}
            </div>
            )}
          </div>

          {/* Sticky Footer */}
          <div className="sticky bottom-0 left-0 w-full bg-card/90 dark:bg-background/80 border-t border-border/60 px-4 sm:px-6 py-4 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="w-full sm:w-auto px-6"
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSchedule}
              disabled={isScheduling}
              className="w-full sm:w-auto px-6 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-md hover:shadow-lg transition-all min-w-[160px]"
            >
              {isScheduling ? <ButtonLoader /> : <><Check className="w-4 h-4 mr-2" />{isEditMode ? t('common.update') : t("meetings.scheduleMeeting")}</>}
            </Button>
          </div>
        </Card>
      </div>

      {/* Right Side - Invites Panel - Desktop Only */}
      {canManageCapacityAndInvitees && (
      <div className="hidden lg:block w-96 flex flex-col gap-6">
        <InvitesPanel
          selectedContacts={meetingData.invitees}
          hostEmail={hostEmail}
          initialPassive={meetingData.passiveInvitees}
          passiveEnabled={meetingData.passiveParticipantsEnabled}
          seats={meetingData.seats}
          onStatusesChange={(m) => setInviteeStatus((prev) => ({ ...prev, ...m }))}
          onContactsChange={(contacts) => setMeetingData((prev) => ({ ...prev, invitees: contacts }))}
        />
      </div>
      )}
    </div>
    </div>

      {/* Time Zone Planner Dialog */}
      <TimeZonePlannerDialog
        open={timezonePlannerOpen}
        onOpenChange={setTimezonePlannerOpen}
        meetingDate={meetingData.date?.toISOString().split('T')[0] || ''}
        meetingTime={meetingData.time}
        meetingTimezone={meetingData.timezone}
        selectedTimezones={meetingData.additionalTimezones}
        onTimezonesChange={(timezones) => setMeetingData({ ...meetingData, additionalTimezones: timezones })}
      />
    </>
  )
}
